import React, { useState, useEffect } from 'react';
import { getPriceHistory, getDirectReferrals } from '../../utils/api';

const Overview = ({ account, balance, price, priceHistory, setPriceHistory, f, fBC, toBC, BC, SYM, vBC, showUsdt }) => {
  const [chartRange, setChartRange] = useState('30');
  const [directCount, setDirectCount] = useState(null);

  // ─── Direct referrals (Level 1) — same source the Network page uses ───
  useEffect(() => {
    if (!account) return;
    let alive = true;
    (async () => {
      try {
        const d = await getDirectReferrals(account);
        if (alive) setDirectCount(d?.directCount ?? (Array.isArray(d?.directs) ? d.directs.length : 0));
      } catch { if (alive) setDirectCount(null); }
    })();
    return () => { alive = false; };
  }, [account]);

  const tp = balance?.tokenPriceInUsdt || 0;
  const bcRate = price?.cryptoPrices?.[BC] || 1;
  const tpBC = toBC(tp);
  const oneBC_tokens = tp > 0 ? (bcRate / tp) : 0;
  const isNotUsdt = BC !== 'USDT';
  // vBC + showUsdt now come from the Dashboard (app-wide toggle).

  // ─── Withdrawable NOW (accurate) ───
  // What you can actually pull right now is limited by BOTH:
  //   (a) the value of the tokens you currently hold (after the sell burn), and
  //   (b) your remaining 2× cap.
  // It is the SMALLER of the two — not just the cap.
  const feePct = (price?.sellFeePercent || 10) / 100;
  const portfolioVal = balance?.portfolioValueUsdt != null ? balance.portfolioValueUsdt : (balance?.tokenValueUsdt || 0);
  const capRemaining = balance?.withdrawRemaining || 0;           // 2× invested − already withdrawn
  const maxWithdrawable = balance?.maxWithdrawable || 0;          // the cap itself (2× invested)
  const sellExempt = balance?.sellExempt === true;
  const isGateMet = balance?.isGateMet != null ? balance.isGateMet : (sellExempt || portfolioVal >= maxWithdrawable);

  // Only 2× of invested is withdrawable, and only ONCE the portfolio has reached
  // that amount (i.e. doubled). Exempt wallets can pull their holdings anytime.
  const withdrawableNow = sellExempt
    ? Math.max(0, portfolioVal * (1 - feePct))
    : (isGateMet ? capRemaining : 0);

  let withdrawSub, withdrawState;
  if (sellExempt) {
    withdrawSub = 'No cap · sell anytime';
    withdrawState = withdrawableNow > 0 ? 'ok' : 'muted';
  } else if (maxWithdrawable <= 0) {
    withdrawSub = `Buy ${SYM} to start`;
    withdrawState = 'muted';
  } else if (!isGateMet) {
    withdrawSub = `Unlocks at ${fBC(maxWithdrawable, 2)} ${BC} · now ${fBC(portfolioVal, 2)}`;
    withdrawState = 'locked';
  } else if (capRemaining <= 1e-9) {
    withdrawSub = 'Fully withdrawn';
    withdrawState = 'muted';
  } else {
    withdrawSub = `Up to ${fBC(maxWithdrawable, 2)} ${BC} · you receive ~${fBC(maxWithdrawable * (1 - feePct), 2)} after ${(feePct * 100).toFixed(0)}% burn`;
    withdrawState = 'ok';
  }

  const claimableVal = balance?.levelIncomeWallet || 0;

  return (
    <div className="fade-in ov-root">
      <style>{`
        .ov-root { width: 100%; box-sizing: border-box; --ov-border: rgba(0,0,0,.10); --ov-border-strong: rgba(0,0,0,.18); }
        .ov-root * { box-sizing: border-box; }
        /* dark-mode aware borders (works with common dark-theme wrappers) */
        @media (prefers-color-scheme: dark) {
          .ov-root { --ov-border: rgba(255,255,255,.14); --ov-border-strong: rgba(255,255,255,.26); }
        }
        html[data-theme="dark"] .ov-root, .dark .ov-root, body.dark .ov-root {
          --ov-border: rgba(255,255,255,.14); --ov-border-strong: rgba(255,255,255,.26);
        }

        /* ── Responsive stats grid ── */
        .ov-root .d-stats-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(165px, 1fr));
          gap: 14px; width: 100%;
        }
        .ov-root .d-stat.card {
          position: relative; display: flex; flex-direction: column; gap: 6px;
          padding: 16px 18px; margin: 0; min-width: 0; border-radius: 14px;
          border: 1px solid var(--ov-border, rgba(0,0,0,.10));
          box-shadow: 0 1px 2px rgba(0,0,0,.04);
          transition: transform .16s ease, box-shadow .16s ease, border-color .16s ease;
        }
        .ov-root .d-stat.card:hover {
          transform: translateY(-2px);
          box-shadow: 0 8px 22px rgba(0,0,0,.09);
          border-color: var(--ov-border-strong, rgba(0,0,0,.18));
        }

        .ov-root .d-stat-label {
          font-size: 11px; font-weight: 600; letter-spacing: .04em;
          text-transform: uppercase; opacity: .72;
        }
        .ov-root .d-stat-val {
          font-size: 24px; font-weight: 700; line-height: 1.1;
          font-variant-numeric: tabular-nums; word-break: break-word;
        }
        .ov-root .d-stat-sub { font-size: 12px; opacity: .78; word-break: break-word; }
        .ov-root .d-stat-sub.is-locked { color: #d97706; opacity: 1; font-weight: 600; }
        .ov-root .d-stat-sub.is-ok { color: #16a34a; opacity: 1; font-weight: 600; }
        .ov-root .d-usdt-eq { font-size: 11px; opacity: .82; }

        /* ── USDT toggle ── */
        .ov-root .d-usdt-toggle {
          display: flex; align-items: center; flex-wrap: wrap; gap: 10px;
          margin-bottom: 12px; font-size: 13px;
        }
        .ov-root .d-toggle-label { display: inline-flex; align-items: center; gap: 6px; cursor: pointer; }

        /* ── Global stats bar ── */
        .ov-root .d-global.card {
          display: flex; flex-wrap: wrap; gap: 14px 30px;
          padding: 16px 20px; margin-top: 16px; border-radius: 14px;
          border: 1px solid var(--ov-border); box-shadow: 0 1px 2px rgba(0,0,0,.04);
        }
        .ov-root .d-global-item { display: flex; flex-direction: column; gap: 3px; min-width: 0; }
        .ov-root .d-global-item span { font-size: 11px; opacity: .7; text-transform: uppercase; letter-spacing: .04em; }
        .ov-root .d-global-item strong { font-size: 15px; font-variant-numeric: tabular-nums; word-break: break-word; }

        /* ── Chart ── */
        .ov-root .d-chart-card {
          margin-top: 16px; padding: 18px; border-radius: 14px;
          border: 1px solid var(--ov-border); box-shadow: 0 1px 2px rgba(0,0,0,.04);
        }
        .ov-root .d-chart-header {
          display: flex; align-items: center; justify-content: space-between;
          flex-wrap: wrap; gap: 10px; margin-bottom: 8px;
        }
        .ov-root .d-chart-title { font-size: 16px; margin: 0; }
        .ov-root .d-chart-range { display: flex; flex-wrap: wrap; gap: 6px; }
        .ov-root .d-range-btn { padding: 5px 11px; font-size: 12px; border-radius: 8px; cursor: pointer; }
        .ov-root .d-chart-current { display: flex; align-items: baseline; gap: 10px; flex-wrap: wrap; margin-bottom: 6px; }
        .ov-root .d-chart-price { font-size: 20px; font-weight: 700; font-variant-numeric: tabular-nums; word-break: break-all; }
        .ov-root .d-chart-change { font-size: 13px; font-weight: 600; }
        .ov-root .d-chart-change.up { color: #16a34a; }
        .ov-root .d-chart-change.down { color: #ef4444; }
        .ov-root .d-chart-wrap { width: 100%; overflow: hidden; }
        .ov-root .d-chart-svg { width: 100%; height: auto; display: block; }
        .ov-root .d-chart-footer {
          display: flex; flex-wrap: wrap; gap: 8px 16px; margin-top: 10px;
          font-size: 11.5px; opacity: .72;
        }

        @media (max-width: 540px) {
          .ov-root .d-stats-grid { grid-template-columns: repeat(2, 1fr); gap: 11px; }
          .ov-root .d-stat-val { font-size: 20px; }
          .ov-root .d-stat.card { padding: 14px 14px; }
        }
      `}</style>

      <div className="d-stats-grid">
        <div className="d-stat card"><span className="d-stat-label">{SYM} Balance</span><span className="d-stat-val">{f(balance?.tokenBalance, 2)}</span><span className="d-stat-sub">{SYM}</span></div>
        <div className="d-stat card"><span className="d-stat-label">1 {SYM} Price</span><span className="d-stat-val">{BC === 'USDT' ? f(tp, 6) : f(tpBC, 8)}</span><span className="d-stat-sub">{BC} per {SYM}{isNotUsdt && showUsdt ? <span className="d-usdt-eq"> ≈ {f(tp, 4)} USDT</span> : ''}</span></div>
        <div className="d-stat card"><span className="d-stat-label">1 {BC} Gets</span><span className="d-stat-val">{f(oneBC_tokens, 2)}</span><span className="d-stat-sub">{SYM}</span></div>
        <div className="d-stat card"><span className="d-stat-label">Portfolio Value</span><span className="d-stat-val">{fBC(balance?.tokenValueUsdt, 4)}</span><span className="d-stat-sub">{vBC(balance?.tokenValueUsdt)}</span></div>
        <div className="d-stat card"><span className="d-stat-label">Total Deposited</span><span className="d-stat-val">{fBC(balance?.totalDeposit, 4)}</span><span className="d-stat-sub">{vBC(balance?.totalDeposit)}</span></div>
        <div className="d-stat card"><span className="d-stat-label">Level Income</span><span className="d-stat-val">{fBC(balance?.levelIncomeEarned, 4)}</span><span className="d-stat-sub">{vBC(balance?.levelIncomeEarned)}</span></div>
        <div className="d-stat card"><span className="d-stat-label">Claimable</span><span className="d-stat-val text-accent">{fBC(claimableVal, 4)}</span><span className="d-stat-sub">{vBC(claimableVal)}</span></div>

        {/* ── Direct Referrals: Level 1 COUNT (from Network endpoint, no USDT) ── */}
        <div className="d-stat card">
          <span className="d-stat-label">Direct Referrals</span>
          <span className="d-stat-val">{directCount != null ? directCount : '—'}</span>
          <span className="d-stat-sub">Level 1 members</span>
        </div>

        {/* ── Withdrawable NOW (honest, lock-aware) ── */}
        <div className="d-stat card">
          <span className="d-stat-label">Withdrawable Now</span>
          <span className="d-stat-val">{fBC(withdrawableNow, 4)}</span>
          <span className={`d-stat-sub${withdrawState === 'locked' ? ' is-locked' : withdrawState === 'ok' ? ' is-ok' : ''}`}>{withdrawSub}</span>
        </div>

        {(balance?.p2pWallet || 0) > 0 && (
          <div className="d-stat card"><span className="d-stat-label">P2P Wallet</span><span className="d-stat-val">{f(balance.p2pWallet, 2)}</span><span className="d-stat-sub">{SYM} ({vBC(balance.p2pWallet * (balance?.tokenPriceInUsdt || 0))}) — invest only</span></div>
        )}
      </div>

      {/* Global token stats card (Supply / Liquidity / Burned / Collection) —
          HIDDEN on the user dashboard for now; uncomment to bring it back.
      {price && (
        <div className="d-global card">
          <div className="d-global-item"><span>Supply</span><strong>{f(price.totalSupply, 2)} {SYM}</strong></div>
          <div className="d-global-item"><span>Liquidity</span><strong>{fBC(price.liquidity, 4)} {BC}{isNotUsdt && showUsdt ? <span className="d-usdt-eq"> ≈ {f(price.liquidity, 2)} USDT</span> : ''}</strong></div>
          <div className="d-global-item"><span>Burned</span><strong>{f(price.totalBurned, 2)} {SYM}</strong></div>
          <div className="d-global-item"><span>Collection</span><strong>{fBC(price.totalCollection, 4)} {BC}{isNotUsdt && showUsdt ? <span className="d-usdt-eq"> ≈ {f(price.totalCollection, 2)} USDT</span> : ''}</strong></div>
          {price.testMode && <div className="d-global-item"><span>Mode</span><strong className="text-accent">TEST MODE</strong></div>}
        </div>
      )}
      */}

      {/* Price Chart */}
      {priceHistory.length > 1 && (
        <div className="card d-chart-card">
          <div className="d-chart-header">
            <h3 className="d-chart-title">{SYM} Price Chart</h3>
            <div className="d-chart-range">
              {[['1', '1D'], ['7', '7D'], ['30', '1M'], ['90', '3M'], ['365', '1Y'], ['9999', 'ALL']].map(([d, label]) => (
                <button key={d} className={`d-range-btn ${String(chartRange) === d ? 'active' : ''}`}
                  onClick={async () => { setChartRange(d); try { const ph = await getPriceHistory(parseInt(d)); setPriceHistory(ph.points || []); } catch {} }}>
                  {label}
                </button>
              ))}
            </div>
          </div>
          <div className="d-chart-current">
            <span className="d-chart-price">{BC === 'USDT' ? f(price?.tokenPriceInUsdt, 8) : f(toBC(price?.tokenPriceInUsdt), 10)} {BC}</span>
            {priceHistory.length >= 2 && (() => {
              const first = toBC(priceHistory[0]?.price || 0);
              const last = toBC(priceHistory[priceHistory.length - 1]?.price || 0);
              const change = first > 0 ? ((last - first) / first * 100) : 0;
              return <span className={`d-chart-change ${change >= 0 ? 'up' : 'down'}`}>{change >= 0 ? '+' : ''}{change.toFixed(2)}%</span>;
            })()}
          </div>
          <div className="d-chart-wrap">
            <svg viewBox="0 0 900 260" className="d-chart-svg" preserveAspectRatio="xMidYMid meet">
              {(() => {
                const pts = priceHistory.map(p => ({ ...p, price: toBC(p.price) }));
                const prices = pts.map(p => p.price);
                const minP = Math.min(...prices);
                const maxP = Math.max(...prices);
                const padding = (maxP - minP) * 0.05 || minP * 0.01;
                const yMin = minP - padding;
                const yMax = maxP + padding;
                const rangeP = yMax - yMin || 1;
                const cW = 780, cH = 210, pL = 5, pT = 10;
                const pathPoints = pts.map((p, i) => { const x = pL + (i / Math.max(pts.length - 1, 1)) * cW; const y = pT + cH - ((p.price - yMin) / rangeP) * cH; return `${i === 0 ? 'M' : 'L'}${x.toFixed(1)},${y.toFixed(1)}`; }).join(' ');
                const fillPts = pts.map((p, i) => { const x = pL + (i / Math.max(pts.length - 1, 1)) * cW; const y = pT + cH - ((p.price - yMin) / rangeP) * cH; return `${x.toFixed(1)},${y.toFixed(1)}`; });
                const fillPath = `M${pL},${pT + cH} ${fillPts.join(' L')} L${(pL + cW).toFixed(1)},${pT + cH} Z`;
                const lastP = prices[prices.length - 1]; const firstP = prices[0];
                const isUp = lastP >= firstP; const color = isUp ? '#00c853' : '#ff1744';
                const labelCount = 5;
                const yLabels = Array.from({ length: labelCount }, (_, i) => yMin + (rangeP * i / (labelCount - 1)));
                const maxDec = Math.max(6, Math.ceil(-Math.log10(rangeP / labelCount)) + 2);
                return (<>
                  <defs><linearGradient id="chartGrad" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor={color} stopOpacity="0.15" /><stop offset="100%" stopColor={color} stopOpacity="0.01" /></linearGradient></defs>
                  {yLabels.map((v, i) => { const y = pT + cH - ((v - yMin) / rangeP) * cH; return (<g key={i}><line x1={pL} y1={y} x2={pL + cW} y2={y} stroke="#e0e0e0" strokeWidth="0.5" strokeDasharray="4,4" /><text x={pL + cW + 8} y={y + 4} fill="#999" fontSize="10" fontFamily="monospace">{v.toFixed(maxDec)}</text></g>); })}
                  <path d={fillPath} fill="url(#chartGrad)" /><path d={pathPoints} fill="none" stroke={color} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
                  {pts.length > 0 && (() => { const x = pL + cW; const y = pT + cH - ((lastP - yMin) / rangeP) * cH; return <><circle cx={x} cy={y} r="5" fill={color} /><circle cx={x} cy={y} r="8" fill={color} opacity="0.2" /></>; })()}
                </>);
              })()}
            </svg>
          </div>
        </div>
      )}
    </div>
  );
};

export default Overview;